import { useEffect, useRef, useState } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const seasons = [
  {
    key: "Winter",
    copy: "Firelight, root cellars, and the long slow burn of the mountain kitchen at rest.",
    dish: "Fire · Root · Game",
    main: "https://readdy.ai/api/search-image?query=Intimate%20alpine%20fine%20dining%20room%20in%20deep%20winter%2C%20open%20wood%20fire%20hearth%20casting%20warm%20amber%20glow%2C%20chef%20plating%20slow-cooked%20game%20on%20dark%20ceramic%2C%20candlelight%2C%20timber%20walls%2C%20quiet%20luxury%20editorial%20restaurant%20photography%2C%20muted%20warm%20palette%2C%20cinematic%20mood&width=1600&height=1800&seq=taste-main-winter&orientation=portrait",
    dishImg: "https://readdy.ai/api/search-image?query=Overhead%20plated%20winter%20alpine%20course%20on%20dark%20handmade%20ceramic%2C%20braised%20venison%20with%20roasted%20root%20vegetables%20and%20dark%20jus%2C%20candlelit%20amber%20mood%2C%20editorial%20fine%20dining%20food%20photography%2C%20quiet%20luxury%2C%20muted%20warm%20palette%2C%20fine%20art%20composition&width=1400&height=1000&seq=taste-dish-winter&orientation=landscape",
  },
  {
    key: "Spring",
    copy: "The first thaw. Wild herbs return to the pass and the kitchen lightens its hand.",
    dish: "Melt · Herb · Fresh cheese",
    main: "https://readdy.ai/api/search-image?query=Bright%20airy%20alpine%20restaurant%20interior%20in%20early%20spring%2C%20soft%20morning%20daylight%20through%20tall%20windows%2C%20chef%20plating%20fresh%20greens%20on%20pale%20ceramic%2C%20linen%20napery%2C%20timber%20and%20stone%2C%20quiet%20luxury%20editorial%20restaurant%20photography%2C%20muted%20pale%20palette%2C%20cinematic%20calm&width=1600&height=1800&seq=taste-main-spring&orientation=portrait",
    dishImg: "https://readdy.ai/api/search-image?query=Overhead%20plated%20spring%20alpine%20course%20on%20pale%20handmade%20ceramic%2C%20wild%20herbs%20young%20greens%20fresh%20mountain%20cheese%20and%20cultured%20cream%2C%20soft%20daylight%2C%20editorial%20fine%20dining%20food%20photography%2C%20quiet%20luxury%2C%20muted%20pale%20palette%2C%20fine%20art%20composition&width=1400&height=1000&seq=taste-dish-spring&orientation=landscape",
  },
  {
    key: "Summer",
    copy: "High meadow air, open-fire cooking, and produce carried up from the valley gardens.",
    dish: "Alpine berry · Trout · Meadow",
    main: "https://readdy.ai/api/search-image?query=Airy%20alpine%20restaurant%20terrace%20in%20high%20summer%2C%20open-fire%20outdoor%20grill%2C%20chef%20cooking%20trout%20over%20embers%2C%20wildflower%20meadow%20beyond%2C%20soft%20golden%20late%20afternoon%20light%2C%20quiet%20luxury%20editorial%20restaurant%20photography%2C%20warm%20natural%20palette%2C%20cinematic%20fresh%20mood&width=1600&height=1800&seq=taste-main-summer&orientation=portrait",
    dishImg: "https://readdy.ai/api/search-image?query=Overhead%20plated%20summer%20alpine%20course%20on%20pale%20ceramic%2C%20grilled%20mountain%20trout%20wild%20berries%20fresh%20meadow%20herbs%20and%20cultured%20butter%2C%20golden%20natural%20daylight%2C%20editorial%20fine%20dining%20food%20photography%2C%20quiet%20luxury%2C%20fresh%20natural%20palette%2C%20fine%20art%20composition&width=1400&height=1000&seq=taste-dish-summer&orientation=landscape",
  },
  {
    key: "Autumn",
    copy: "Harvest smoke drifts from the cellar. Mushrooms, larch, and the last long light of the year.",
    dish: "Larch · Mushroom · Smoke",
    main: "https://readdy.ai/api/search-image?query=Warm%20alpine%20cellar%20dining%20room%20in%20late%20autumn%2C%20golden%20larch%20light%20through%20small%20windows%2C%20chef%20plating%20forest%20mushroom%20course%20on%20dark%20ceramic%2C%20hanging%20smoked%20meats%2C%20timber%20and%20stone%2C%20quiet%20luxury%20editorial%20restaurant%20photography%2C%20rich%20amber%20palette%2C%20cinematic%20harvest%20mood&width=1600&height=1800&seq=taste-main-autumn&orientation=portrait",
    dishImg: "https://readdy.ai/api/search-image?query=Overhead%20plated%20autumn%20alpine%20course%20on%20dark%20ceramic%2C%20wild%20forest%20mushrooms%20smoked%20game%20larch%20oil%20and%20fermented%20jus%2C%20warm%20amber%20light%2C%20editorial%20fine%20dining%20food%20photography%2C%20quiet%20luxury%2C%20rich%20earthy%20palette%2C%20fine%20art%20composition&width=1400&height=1000&seq=taste-dish-autumn&orientation=landscape",
  },
];

export default function TasteSeasons() {
  const ref = useRef<HTMLElement>(null);
  const [season, setSeason] = useState(0);
  const current = seasons[season];

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.utils.toArray<HTMLElement>(".taste-reveal").forEach((el) => {
        gsap.fromTo(
          el,
          { opacity: 0, y: 40 },
          {
            opacity: 1,
            y: 0,
            duration: 1.1,
            ease: "power3.out",
            scrollTrigger: { trigger: el, start: "top 85%" },
          }
        );
      });
    }, ref);
    return () => ctx.revert();
  }, []);

  return (
    <section
      id="taste"
      ref={ref}
      className="relative bg-background-100 text-foreground-950 py-20 md:py-28 px-6 md:px-10 overflow-hidden"
    >
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center gap-4 taste-reveal">
          <span className="w-8 h-px bg-foreground-950/40" />
          <span className="eyebrow text-foreground-700">06 — Taste & Seasons</span>
        </div>

        <div className="grid md:grid-cols-12 gap-8 md:gap-16 mt-8">
          <h2 className="taste-reveal md:col-span-8 font-display font-light text-3xl md:text-[42px] leading-[1.08] tracking-tight text-foreground-950">
            Cooked over honest fire.
          </h2>
          <p className="taste-reveal md:col-span-4 text-foreground-700 text-[15px] font-light leading-relaxed max-w-md">
            Our kitchen follows the mountain calendar. Every plate begins within forty kilometres — from the forest floor, the pasture, the smokehouse.
          </p>
        </div>

        <div className="grid md:grid-cols-12 gap-8 mt-10 md:mt-12 md:h-[600px]">
          {/* Left main image */}
          <div className="taste-reveal md:col-span-7 h-[440px] md:h-full overflow-hidden relative">
            {seasons.map((s, i) => (
              <img
                key={s.key}
                src={s.main}
                alt={`${s.key} dining`}
                className="absolute inset-0 w-full h-full object-cover object-top transition-opacity duration-700 ease-out"
                style={{ opacity: i === season ? 1 : 0 }}
              />
            ))}
          </div>

          {/* Right column: text on top, dish image bottom-aligned */}
          <div className="md:col-span-5 flex flex-col md:h-full">
            <div className="taste-reveal flex-1 flex flex-col">
              <div className="eyebrow text-foreground-700 mb-4">Select season</div>
              <div className="flex flex-wrap gap-2">
                {seasons.map((s, i) => (
                  <button
                    key={s.key}
                    onClick={() => setSeason(i)}
                    className={`eyebrow whitespace-nowrap px-4 py-2.5 border transition-colors cursor-pointer ${
                      i === season
                        ? "bg-foreground-950 border-foreground-950 text-background-50"
                        : "border-foreground-950/25 text-foreground-700 hover:text-foreground-950 hover:border-foreground-950/60"
                    }`}
                  >
                    {s.key}
                  </button>
                ))}
              </div>

              <div key={season} className="mt-6 border-t border-foreground-950/15 pt-5 animate-[tasteFade_.55s_ease]">
                <p className="text-foreground-700 text-[15px] font-light leading-relaxed">
                  {current.copy}
                </p>
                <div className="eyebrow text-foreground-700 mt-6">On the pass</div>
                <p className="font-display text-lg md:text-xl mt-2 text-foreground-950 font-light leading-snug">
                  {current.dish}
                </p>
              </div>
            </div>

            <div className="taste-reveal h-[240px] md:h-[300px] mt-6 overflow-hidden relative">
              {seasons.map((s, i) => (
                <img
                  key={s.key}
                  src={s.dishImg}
                  alt={`${s.key} dish`}
                  className="absolute inset-0 w-full h-full object-cover object-top transition-opacity duration-700 ease-out"
                  style={{ opacity: i === season ? 1 : 0 }}
                />
              ))}
            </div>
          </div>
        </div>

        <div className="taste-reveal grid md:grid-cols-4 gap-6 md:gap-8 mt-12 border-t border-foreground-950/15 pt-8">
          {[
            { k: "The Fire Room", v: "Nine-course tasting menu, nightly" },
            { k: "The Larder", v: "All-day counter, wood-fired breads" },
            { k: "The Cellar", v: "260 low-intervention wines" },
            { k: "Private", v: "Chef's table for six, by request" },
          ].map((f) => (
            <div key={f.k}>
              <div className="eyebrow text-foreground-700">{f.k}</div>
              <p className="mt-2 font-display text-[15px] md:text-base text-foreground-950 leading-snug font-light">
                {f.v}
              </p>
            </div>
          ))}
        </div>
      </div>

      <style>{`
        @keyframes tasteFade { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: none; } }
      `}</style>
    </section>
  );
}